/**
 * Compliance Data Validation Utilities
 * 
 * Provides comprehensive validation for compliance API responses and data structures
 * to prevent runtime errors from malformed backend responses.
 */

import type { 
  ComplianceIssue, 
  ComplianceResponse, 
  LegacyComplianceResponse, 
  EntitySummary 
} from '../../services/compliance.service';

/**
 * Logger for validation warnings and errors
 */
const logger = {
  warn: (message: string, data?: any) => {
    console.warn(`[ComplianceValidation] ${message}`, data);
  },
  error: (message: string, data?: any) => {
    console.error(`[ComplianceValidation] ${message}`, data);
  }
};

/**
 * Validates that a string is a proper hex hash (32 or 64 characters)
 * Temporarily accepts both formats during migration from 32 to 64 char hashes
 */
export function isValidIssueHash(hash: any): hash is string {
  if (typeof hash !== 'string') return false;
  
  // Support both 32-char (legacy) and 64-char (current) hashes
  const is32Char = /^[a-f0-9]{32}$/.test(hash);
  const is64Char = /^[a-f0-9]{64}$/.test(hash);
  
  return is32Char || is64Char;
}

/**
 * Validates that a severity value is one of the expected values
 */
export function isValidSeverity(severity: any): severity is 'HIGH' | 'MEDIUM' | 'LOW' {
  return typeof severity === 'string' && 
         ['HIGH', 'MEDIUM', 'LOW'].includes(severity);
}

/**
 * Validates that a status value is one of the expected values
 */
export function isValidStatus(status: any): status is 'review' | 'fix' | 'ok' {
  return typeof status === 'string' && 
         ['review', 'fix', 'ok'].includes(status);
}

/**
 * Validates that a date string is properly formatted
 */
export function isValidDateString(date: any): date is string {
  if (typeof date !== 'string') return false;
  
  try {
    const parsed = new Date(date);
    return !isNaN(parsed.getTime());
  } catch {
    return false;
  }
}

/**
 * Type guard for ComplianceIssue with comprehensive validation
 */
export function isValidComplianceIssue(issue: any): issue is ComplianceIssue {
  if (!issue || typeof issue !== 'object') {
    logger.warn('Issue is not an object', issue);
    return false;
  }

  // Required fields validation
  if (!isValidIssueHash(issue.issue_hash)) {
    logger.warn('Invalid issue_hash', { issue_hash: issue.issue_hash, issue });
    return false;
  }

  if (typeof issue.issue_type !== 'string' || !issue.issue_type.trim()) {
    logger.warn('Invalid issue_type', { issue_type: issue.issue_type, issue });
    return false;
  }

  if (typeof issue.type !== 'string' || !issue.type.trim()) {
    logger.warn('Invalid type field', { type: issue.type, issue });
    return false;
  }

  if (!isValidSeverity(issue.severity)) {
    logger.warn('Invalid severity', { severity: issue.severity, issue });
    return false;
  }

  if (!isValidStatus(issue.status)) {
    logger.warn('Invalid status', { status: issue.status, issue });
    return false;
  }

  if (typeof issue.title !== 'string' || !issue.title.trim()) {
    logger.warn('Invalid title', { title: issue.title, issue });
    return false;
  }

  if (typeof issue.description !== 'string' || !issue.description.trim()) {
    logger.warn('Invalid description', { description: issue.description, issue });
    return false;
  }

  // Optional fields validation
  if (issue.entity_id !== undefined && (typeof issue.entity_id !== 'string' || !issue.entity_id.trim())) {
    logger.warn('Invalid entity_id', { entity_id: issue.entity_id, issue });
    return false;
  }

  if (issue.entity_name !== undefined && (typeof issue.entity_name !== 'string' || !issue.entity_name.trim())) {
    logger.warn('Invalid entity_name', { entity_name: issue.entity_name, issue });
    return false;
  }

  if (issue.source_transaction_date !== undefined && !isValidDateString(issue.source_transaction_date)) {
    logger.warn('Invalid source_transaction_date', { source_transaction_date: issue.source_transaction_date, issue });
    return false;
  }

  if (issue.detected_at !== undefined && !isValidDateString(issue.detected_at)) {
    logger.warn('Invalid detected_at', { detected_at: issue.detected_at, issue });
    return false;
  }

  if (issue.first_detected_at !== undefined && !isValidDateString(issue.first_detected_at)) {
    logger.warn('Invalid first_detected_at', { first_detected_at: issue.first_detected_at, issue });
    return false;
  }

  if (issue.last_status_change !== undefined && !isValidDateString(issue.last_status_change)) {
    logger.warn('Invalid last_status_change', { last_status_change: issue.last_status_change, issue });
    return false;
  }

  if (issue.detection_count !== undefined && (typeof issue.detection_count !== 'number' || issue.detection_count < 1)) {
    logger.warn('Invalid detection_count', { detection_count: issue.detection_count, issue });
    return false;
  }

  if (issue.related_transaction_ids !== undefined && !Array.isArray(issue.related_transaction_ids)) {
    logger.warn('Invalid related_transaction_ids', { related_transaction_ids: issue.related_transaction_ids, issue });
    return false;
  }

  return true;
}

/**
 * Validates and filters an array of compliance issues
 */
export function validateAndFilterIssues(issues: any[]): ComplianceIssue[] {
  if (!Array.isArray(issues)) {
    logger.error('Issues is not an array', issues);
    return [];
  }

  const validIssues: ComplianceIssue[] = [];
  const invalidIssues: any[] = [];

  issues.forEach((issue, index) => {
    if (isValidComplianceIssue(issue)) {
      validIssues.push(issue);
    } else {
      invalidIssues.push({ index, issue });
    }
  });

  if (invalidIssues.length > 0) {
    logger.warn(`Filtered out ${invalidIssues.length} invalid issues out of ${issues.length} total`, {
      invalidCount: invalidIssues.length,
      totalCount: issues.length,
      firstInvalidIssue: invalidIssues[0]
    });
  }

  return validIssues;
}

/**
 * Type guard for ComplianceResponse with validation
 */
export function isValidComplianceResponse(response: any): response is ComplianceResponse {
  if (!response || typeof response !== 'object') {
    logger.error('Response is not an object', response);
    return false;
  }

  if (typeof response.entity_id !== 'string' || !response.entity_id.trim()) {
    logger.error('Invalid entity_id in response', { entity_id: response.entity_id });
    return false;
  }

  if (!Array.isArray(response.active_issues)) {
    logger.error('active_issues is not an array', { active_issues: response.active_issues });
    return false;
  }

  if (!Array.isArray(response.dismissed_issues)) {
    logger.error('dismissed_issues is not an array', { dismissed_issues: response.dismissed_issues });
    return false;
  }

  return true;
}

/**
 * Type guard for LegacyComplianceResponse with validation
 */
export function isValidLegacyComplianceResponse(response: any): response is LegacyComplianceResponse {
  if (!response || typeof response !== 'object') {
    logger.error('Legacy response is not an object', response);
    return false;
  }

  if (typeof response.success !== 'boolean') {
    logger.error('Invalid success field in legacy response', { success: response.success });
    return false;
  }

  if (typeof response.total_issues !== 'number' || response.total_issues < 0) {
    logger.error('Invalid total_issues in legacy response', { total_issues: response.total_issues });
    return false;
  }

  if (typeof response.filtered_issues !== 'number' || response.filtered_issues < 0) {
    logger.error('Invalid filtered_issues in legacy response', { filtered_issues: response.filtered_issues });
    return false;
  }

  if (!Array.isArray(response.issues)) {
    logger.error('Issues is not an array in legacy response', { issues: response.issues });
    return false;
  }

  // Validate pagination object
  if (!response.pagination || typeof response.pagination !== 'object') {
    logger.error('Invalid pagination object in legacy response', { pagination: response.pagination });
    return false;
  }

  const pagination = response.pagination;
  if (typeof pagination.total !== 'number' || pagination.total < 0 ||
      typeof pagination.page !== 'number' || pagination.page < 1 ||
      typeof pagination.per_page !== 'number' || pagination.per_page < 1 ||
      typeof pagination.pages !== 'number' || pagination.pages < 0 ||
      typeof pagination.has_next !== 'boolean' ||
      typeof pagination.has_prev !== 'boolean') {
    logger.error('Invalid pagination fields in legacy response', { pagination });
    return false;
  }

  return true;
}

/**
 * Validates and sanitizes a ComplianceResponse
 */
export function validateComplianceResponse(response: any): ComplianceResponse | null {
  if (!isValidComplianceResponse(response)) {
    return null;
  }

  // Validate and filter issues arrays
  const validActiveIssues = validateAndFilterIssues(response.active_issues);
  const validDismissedIssues = validateAndFilterIssues(response.dismissed_issues);

  return {
    entity_id: response.entity_id,
    active_issues: validActiveIssues,
    dismissed_issues: validDismissedIssues
  };
}

/**
 * Validates and sanitizes a LegacyComplianceResponse
 */
export function validateLegacyComplianceResponse(response: any): LegacyComplianceResponse | null {
  if (!isValidLegacyComplianceResponse(response)) {
    return null;
  }

  // Validate and filter issues array
  const validIssues = validateAndFilterIssues(response.issues);

  return {
    ...response,
    issues: validIssues,
    // Update counts based on filtered issues
    filtered_issues: Math.min(validIssues.length, response.filtered_issues)
  };
}

/**
 * Type guard for EntitySummary with validation
 */
export function isValidEntitySummary(entity: any): entity is EntitySummary {
  if (!entity || typeof entity !== 'object') {
    return false;
  }

  return typeof entity.entity_id === 'string' &&
         entity.entity_id.trim().length > 0 &&
         typeof entity.entity_name === 'string' &&
         entity.entity_name.trim().length > 0 &&
         typeof entity.total_issues === 'number' &&
         entity.total_issues >= 0 &&
         typeof entity.high_severity === 'number' &&
         entity.high_severity >= 0 &&
         typeof entity.medium_severity === 'number' &&
         entity.medium_severity >= 0 &&
         typeof entity.low_severity === 'number' &&
         entity.low_severity >= 0 &&
         entity.types &&
         typeof entity.types === 'object';
}

/**
 * Validates and filters an array of entity summaries
 */
export function validateAndFilterEntitySummaries(entities: any[]): EntitySummary[] {
  if (!Array.isArray(entities)) {
    logger.error('Entities is not an array', entities);
    return [];
  }

  return entities.filter((entity, index) => {
    if (!isValidEntitySummary(entity)) {
      logger.warn(`Invalid entity summary at index ${index}`, entity);
      return false;
    }
    return true;
  });
}

/**
 * Creates a safe fallback ComplianceResponse for error cases
 */
export function createFallbackComplianceResponse(entityId: string): ComplianceResponse {
  return {
    entity_id: entityId,
    active_issues: [],
    dismissed_issues: []
  };
}

/**
 * Creates a safe fallback LegacyComplianceResponse for error cases
 */
export function createFallbackLegacyComplianceResponse(): LegacyComplianceResponse {
  return {
    success: false,
    total_issues: 0,
    filtered_issues: 0,
    applied_filters: { limit: 0, offset: 0 },
    pagination: {
      total: 0,
      page: 1,
      per_page: 50,
      pages: 0,
      has_next: false,
      has_prev: false
    },
    issues: []
  };
}

/**
 * Comprehensive validation result interface
 */
export interface ValidationResult<T> {
  isValid: boolean;
  data: T | null;
  errors: string[];
  warnings: string[];
}

/**
 * Performs comprehensive validation of any compliance response
 */
export function validateComplianceData(response: any): ValidationResult<ComplianceResponse | LegacyComplianceResponse> {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!response) {
    errors.push('Response is null or undefined');
    return { isValid: false, data: null, errors, warnings };
  }

  // Try to validate as ComplianceResponse first
  if ('active_issues' in response && 'dismissed_issues' in response) {
    const validatedResponse = validateComplianceResponse(response);
    
    if (validatedResponse) {
      const originalActiveCount = Array.isArray(response.active_issues) ? response.active_issues.length : 0;
      const originalDismissedCount = Array.isArray(response.dismissed_issues) ? response.dismissed_issues.length : 0;
      const validActiveCount = validatedResponse.active_issues.length;
      const validDismissedCount = validatedResponse.dismissed_issues.length;

      if (validActiveCount < originalActiveCount) {
        warnings.push(`Filtered out ${originalActiveCount - validActiveCount} invalid active issues`);
      }

      if (validDismissedCount < originalDismissedCount) {
        warnings.push(`Filtered out ${originalDismissedCount - validDismissedCount} invalid dismissed issues`);
      }

      return { isValid: true, data: validatedResponse, errors, warnings };
    } else {
      errors.push('Invalid ComplianceResponse structure');
    }
  }

  // Try to validate as LegacyComplianceResponse
  if ('success' in response && 'issues' in response) {
    const validatedResponse = validateLegacyComplianceResponse(response);
    
    if (validatedResponse) {
      const originalIssueCount = Array.isArray(response.issues) ? response.issues.length : 0;
      const validIssueCount = validatedResponse.issues.length;

      if (validIssueCount < originalIssueCount) {
        warnings.push(`Filtered out ${originalIssueCount - validIssueCount} invalid issues`);
      }

      return { isValid: true, data: validatedResponse, errors, warnings };
    } else {
      errors.push('Invalid LegacyComplianceResponse structure');
    }
  }

  errors.push('Response does not match any known compliance response format');
  return { isValid: false, data: null, errors, warnings };
}